import PptxGenJS from "pptxgenjs";
import coverImg from "../assets/images/cover_horizontal.png";
import contentBgImg from "../assets/images/pages_horizontal.png";
import { captureDashboardBlocks } from "./captureDashboardBlocks";

function hexNoHash(hex) {
  return String(hex || "").replace("#", "").toUpperCase();
}

function getPptChartScale() {
  return 0.88;
}

function hexToRgb(hex) {
  const normalized = hexNoHash(hex);

  if (normalized.length !== 6) {
    return { r: 255, g: 255, b: 255 };
  }

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function isNearBackground(r, g, b, a, bg, tolerance = 12) {
  if (a === 0) return true;

  return (
    Math.abs(r - bg.r) <= tolerance &&
    Math.abs(g - bg.g) <= tolerance &&
    Math.abs(b - bg.b) <= tolerance
  );
}

async function trimPageImageToContent(
  page,
  {
    backgroundColor,
    paddingX = 36,
    paddingY = 32,
    extraLeft = 84,
    extraRight = 28,
    extraBottom = 10,
  } = {}
) {
  if (!page?.data || !page?.width || !page?.height) return page;

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = page.data;
  });

  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = page.width;
  srcCanvas.height = page.height;

  const srcCtx = srcCanvas.getContext("2d", { willReadFrequently: true });
  if (!srcCtx) return page;

  srcCtx.drawImage(img, 0, 0, page.width, page.height);

  const imageData = srcCtx.getImageData(0, 0, page.width, page.height);
  const pixels = imageData.data;
  const bg = hexToRgb(backgroundColor || "#ffffff");

  let minX = page.width;
  let minY = page.height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < page.height; y++) {
    for (let x = 0; x < page.width; x++) {
      const idx = (y * page.width + x) * 4;
      const r = pixels[idx];
      const g = pixels[idx + 1];
      const b = pixels[idx + 2];
      const a = pixels[idx + 3];

      if (isNearBackground(r, g, b, a, bg)) continue;

      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < minX || maxY < minY) return page;

  const left = Math.max(0, minX - paddingX - extraLeft);
  const top = Math.max(0, minY - paddingY);
  const right = Math.min(page.width, maxX + paddingX + extraRight);
  const bottom = Math.min(page.height, maxY + paddingY + extraBottom);

  const trimmedWidth = Math.max(1, right - left);
  const trimmedHeight = Math.max(1, bottom - top);

  const outCanvas = document.createElement("canvas");
  outCanvas.width = trimmedWidth;
  outCanvas.height = trimmedHeight;

  const outCtx = outCanvas.getContext("2d");
  if (!outCtx) return page;

  outCtx.drawImage(
    srcCanvas,
    left,
    top,
    trimmedWidth,
    trimmedHeight,
    0,
    0,
    trimmedWidth,
    trimmedHeight
  );

  return {
    ...page,
    data: outCanvas.toDataURL("image/png", 1),
    width: trimmedWidth,
    height: trimmedHeight,
    type: "PNG",
  };
}

async function urlToDataUrl(url, signal) {
  if (!url) return null;

  if (typeof url === "string" && url.startsWith("data:")) return url;

  const res = await fetch(url, { signal });
  const blob = await res.blob();

  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  });
}

export async function exportDashboardPPTX({
  filename = "relatorio",
  cover = coverImg,
  contentBg = contentBgImg,
  backgroundColor = "#ffffff",
  scale = 2,
  clientName = "Cliente",
  clientBrand,
  periodText = "Período",
  titleText = "Relatório de mídia",
  titles = null,
  titleColor = "#614a9e",
  coverTitleColor = "#614a9e",
  coverMetaColor = "#374151",
  slidePadding = 0.55,
  titleBoxH = 0.55,
  titleFontSize = 20,
  titleLeftOffset = 0.95,
  tocColumns = 2,
  showPagination = true,
  pageNumberColor = "#6B7280",
  pageNumberSize = 10,
  notesText = "escreva suas observações aqui.",
  notesFontSize = 12,
  notesColor = "#111827",
  notesItalic = true,
  notesH = 0.35,
  notesGap = 0.16,
  chartLift = 0.28,
  selectedTitles = null,
  onProgress,
  signal,
} = {}) {
  const report = (progress, step) => {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    if (typeof onProgress === "function") onProgress({ progress, step });
  };

  report(1, "Preparando fundos…");

  const coverData = await urlToDataUrl(cover, signal);
  const contentBgData = await urlToDataUrl(contentBg);
  const brandData = await urlToDataUrl(clientBrand);

  report(6, "Capturando dashboard…");
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  const pages = await captureDashboardBlocks({
    selector: "#pdf-dashboard",
    scale,
    backgroundColor,
    captureConcurrency: 2,
    imageFormat: "PNG",
    selectedTitles,
    onProgress: ({ progress, step }) => report(Math.min(88, progress), step),
    signal,
  });

  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  report(90, "Montando PPTX…");

  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";

  const SLIDE_W = 13.333;
  const SLIDE_H = 7.5;

  const FIRST_CONTENT_SLIDE_NUMBER = 3;

  const innerX = slidePadding - 0.35;
  const innerY = slidePadding;
  const innerW = SLIDE_W - slidePadding * 2;
  const innerH = SLIDE_H - slidePadding * 2;

  function addBackground(slide) {
    if (contentBgData) {
      slide.addImage({ data: contentBgData, x: 0, y: 0, w: SLIDE_W, h: SLIDE_H });
    } else if (backgroundColor) {
      slide.background = { color: hexNoHash(backgroundColor) };
    }
  }

  function addPagination(slide, slideNumber, totalSlides) {
    if (!showPagination) return;

    const PAGE_BOTTOM_OFFSET = 0.02;

    slide.addText(`${slideNumber}/${totalSlides}`, {
      x: SLIDE_W - slidePadding - 1.2,
      y: SLIDE_H - slidePadding + PAGE_BOTTOM_OFFSET,
      w: 1.2,
      h: 0.25,
      fontFace: "Mulish",
      fontSize: pageNumberSize,
      color: hexNoHash(pageNumberColor),
      align: "right",
    });
  }

  function addNotes(slide, x, y, w) {
    slide.addText(notesText, {
      x,
      y,
      w,
      h: notesH,
      fontFace: "Mulish",
      fontSize: notesFontSize,
      color: hexNoHash(notesColor),
      italic: !!notesItalic,
    });
  }

  {
    const slide = pptx.addSlide();

    if (coverData) {
      slide.addImage({ data: coverData, x: 0, y: 0, w: SLIDE_W, h: SLIDE_H });
    } else if (backgroundColor) {
      slide.background = { color: hexNoHash(backgroundColor) };
    }

    if (brandData) {
      const marginX = 0.80;
      const marginY = 0.20;

      const maxW = 2.2;
      const maxH = 0.75;

      slide.addImage({
        data: brandData,
        x: marginX,
        y: marginY,
        w: maxW,
        h: maxH,
      });
    }

    const blockH = 0.75 + 0.55 + 0.45 + 0.25;
    const baseY = (SLIDE_H - blockH) / 2;

    slide.addText(titleText, {
      x: 0.9,
      y: baseY,
      w: 12,
      h: 0.8,
      fontFace: "Mulish",
      fontSize: 40,
      bold: true,
      color: hexNoHash(coverTitleColor),
    });

    slide.addText(clientName, {
      x: 0.9,
      y: baseY + 0.85,
      w: 12,
      h: 0.6,
      fontFace: "Mulish",
      fontSize: 22,
      bold: true,
      color: hexNoHash(coverTitleColor),
    });

    slide.addText(periodText, {
      x: 0.9,
      y: baseY + 1.35,
      w: 12,
      h: 0.45,
      fontFace: "Mulish",
      fontSize: 14,
      color: hexNoHash(coverMetaColor),
    });
  }

  {
    const slide = pptx.addSlide();
    addBackground(slide);

    const TITLE_TOP_OFFSET = 0.25;

    slide.addText("Sumário", {
      x: innerX + titleLeftOffset,
      y: innerY + TITLE_TOP_OFFSET,
      w: innerW - titleLeftOffset,
      h: 0.7,
      fontFace: "Mulish",
      fontSize: 28,
      bold: true,
      color: hexNoHash(titleColor),
    });

    slide.addText(`${clientName} • ${periodText}`, {
      x: innerX + titleLeftOffset,
      y: innerY + 0.7,
      w: innerW - titleLeftOffset,
      h: 0.35,
      fontFace: "Mulish",
      fontSize: 12,
      color: hexNoHash("#6B7280"),
    });

    const items = pages.map((p, i) => {
      const t =
        (Array.isArray(titles) && titles[i]) ||
        p?.title ||
        (p.kind?.includes("kpis") ? "Resumo" : "Página");
      const targetSlide = FIRST_CONTENT_SLIDE_NUMBER + i;
      return { text: t, targetSlide };
    });

    const startY = innerY + 1.25;
    const lineH = 0.38;

    const columns = Math.max(1, tocColumns);
    const colGap = 0.6;

    const colW = (innerW - titleLeftOffset - colGap * (columns - 1)) / columns;
    const colX0 = innerX + titleLeftOffset;

    const maxLinesPerCol = Math.floor((SLIDE_H - startY - slidePadding - 0.6) / lineH);
    const itemsPerCol = Math.min(maxLinesPerCol, Math.ceil(items.length / columns));

    for (let col = 0; col < columns; col++) {
      for (let row = 0; row < itemsPerCol; row++) {
        const idx = col * itemsPerCol + row;
        if (idx >= items.length) break;

        const it = items[idx];
        const y = startY + row * lineH;
        const x = colX0 + col * (colW + colGap);

        slide.addText(it.text, {
          x,
          y,
          w: colW - 0.5,
          h: lineH,
          fontFace: "Mulish",
          fontSize: 12,
          color: hexNoHash("#614a9e"),
          underline: true,
          hyperlink: { slide: it.targetSlide },
        });

        slide.addText(`${it.targetSlide}`, {
          x: x + colW - 0.5,
          y,
          w: 0.5,
          h: lineH,
          fontFace: "Mulish",
          fontSize: 12,
          color: hexNoHash("#6B7280"),
          align: "right",
        });
      }
    }

    addPagination(slide, 2, 2 + pages.length);
  }

  const totalSlides = 2 + pages.length;

  for (let i = 0; i < pages.length; i++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const p = pages[i];
    const slideNumber = FIRST_CONTENT_SLIDE_NUMBER + i;
    const slide = pptx.addSlide();

    addBackground(slide);

    const pageTitle =
      (Array.isArray(titles) && titles[i]) ||
      p?.title ||
      (p.kind?.includes("kpis") ? "Resumo" : "Gráfico");

    const pageForPpt = await trimPageImageToContent(p, {
      backgroundColor,
    });

    const TITLE_TOP_OFFSET = 0.35;

    slide.addText(pageTitle, {
      x: innerX + titleLeftOffset,
      y: innerY + TITLE_TOP_OFFSET,
      w: innerW - titleLeftOffset,
      h: titleBoxH,
      fontFace: "Mulish",
      fontSize: titleFontSize,
      bold: true,
      color: hexNoHash(titleColor),
    });

    const imgAreaX = innerX + titleLeftOffset;
    const imgAreaY = innerY + titleBoxH + 0.70 - chartLift;
    const NOTES_H = notesH;        
    const NOTES_GAP = notesGap;     
    const reservedBottom = NOTES_GAP + NOTES_H;
    const chartAreaW = innerW - titleLeftOffset;
    const chartAreaH = (innerH - titleBoxH - 0.18) - reservedBottom;
    const ratio = pageForPpt.height / pageForPpt.width;
    let w = chartAreaW;
    let h = w * ratio;

    if (h > chartAreaH) {
      h = chartAreaH;
      w = h / ratio;
    }

    const MAX_SCALE = getPptChartScale();
    const maxW = chartAreaW * MAX_SCALE;
    const maxH = chartAreaH * MAX_SCALE;

    if (w > maxW) {
      w = maxW;
      h = w * ratio;
    }
    if (h > maxH) {
      h = maxH;
      w = h / ratio;
    }

    const x = imgAreaX + (chartAreaW - w) / 2;
    const y = imgAreaY; 

    slide.addImage({ data: pageForPpt.data, x, y, w, h });

    addNotes(slide, imgAreaX, y + h + NOTES_GAP, chartAreaW);

    addPagination(slide, slideNumber, totalSlides);

    const pct = 90 + Math.round(((i + 1) / Math.max(1, pages.length)) * 9);
    report(Math.min(99, pct), `Montando slide ${i + 1}/${pages.length}…`);
  }

  report(99, "Finalizando…");

  const blob = await pptx.write("blob");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.pptx`;
  a.click();
  URL.revokeObjectURL(url);

  report(100, "Concluído!");
}
